import {Injectable, signal} from '@angular/core';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration: number;
  timestamp: Date;
}

/**
 * Service de notifications Toast moderne
 * Gère l'affichage de notifications temporaires à l'utilisateur
 */
@Injectable({
  providedIn: 'root'
})
export class ToastService {

  // Signal réactif pour la liste des toasts
  toasts = signal<Toast[]>([]);

  /**
   * Affiche un toast de succès
   */
  success(title: string, message: string = '', duration: number = 4000): void {
    this.show('success', title, message, duration);
  }

  /**
   * Affiche un toast d'erreur
   */
  error(title: string, message: string = '', duration: number = 6000): void {
    this.show('error', title, message, duration);
  }

  /**
   * Affiche un toast d'avertissement
   */
  warning(title: string, message: string = '', duration: number = 5000): void {
    this.show('warning', title, message, duration);
  }

  /**
   * Affiche un toast d'information
   */
  info(title: string, message: string = '', duration: number = 4000): void {
    this.show('info', title, message, duration);
  }

  /**
   * Affiche un toast personnalisé
   */
  show(type: Toast['type'], title: string, message: string, duration: number): void {
    const toast: Toast = {
      id: this.generateId(),
      type,
      title,
      message,
      duration,
      timestamp: new Date()
    };

    // Ajouter le toast à la liste
    this.toasts.update(toasts => [...toasts, toast]);

    // Retirer automatiquement après la durée spécifiée
    if (duration > 0) {
      setTimeout(() => this.remove(toast.id), duration);
    }
  }

  /**
   * Retire un toast spécifique
   */
  remove(id: string): void {
    this.toasts.update(toasts => toasts.filter(t => t.id !== id));
  }

  /**
   * Retire tous les toasts
   */
  clearAll(): void {
    this.toasts.set([]);
  }

  /**
   * Génère un ID unique pour un toast
   */
  private generateId(): string {
    return `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
